/**
 * Prebid.js configuration
 * @module lib/prebidConfig
 */

// `userIds` is the consent-gated list the ads initializer built
// (lib/adIdentityConfig.js): empty or absent means no id module, no secure
// signal registration and no user-id syncs — the privacy-safe default.
function configurePrebid(pbjs, userIds) {
  const hasUserIds = Array.isArray(userIds) && userIds.length > 0;
  const userSyncConfig = {
    filterSettings: {
      image: {
        bidders: [
          'ix',
          'appnexus',
          'pubmatic',
          'rubicon',
          'yahoossp',
          'ttd',
          'teads'
        ],
        filter: 'include'
      }
    },
    syncsPerBidder: 3,
    auctionDelay: 1500
  };

  if (hasUserIds) {
    userSyncConfig.userIds = userIds;
    // GAM secure signal: the standard sharedId only, and only while the
    // visitor's advertising consent allows id modules at all.
    userSyncConfig.encryptedSignalSources = {
      sources: [
        {
          source: ['pubcid.org'],
          encrypt: false
        }
      ],
      registerDelay: 1500
    };
  }

  pbjs.setConfig({
    cache: {
      url: 'https://prebid.adnxs.com/pbc/v1/cache',
      ignoreBidderCacheKey: true
    },
    priceGranularity: 'high',
    cpmRoundingFunction: Math.round,
    currency: {
      adServerCurrency: 'CAD',
      defaultRates: { USD: { CAD: 1.38 } }
    },
    enableTIDs: true,
    useBidCache: true,
    bidCacheFilterFunction: function (bid) {
      // Only cache bids above $0.50 CAD to ensure quality
      return bid.cpm > 0.5;
    },
    enableSendAllBids: false,
    targetingControls: {
      alwaysIncludeDeals: true
    },
    // `userIdTargeting` was removed 2026-09-09: the module is not in the
    // Prebid build (the line was inert) and GAM receives our derived PPID
    // through setPublisherProvidedId, never user ids as key-values.
    bidderSequence: 'random',
    bidViewability: {
      enabled: true,
      firePixels: true,
      customMatchFunction: function (bid, slot) {
        return !!(
          bid
          && slot
          && typeof slot.getSlotElementId === 'function'
          && bid.adUnitCode === slot.getSlotElementId()
        );
      }
    },
    userSync: userSyncConfig
  });

  pbjs.bidderSettings = {
    standard: {
      storageAllowed: true,
      adserverTargeting: [
        {
          key: 'hb_bidder',
          val: function (bidResponse) {
            return bidResponse.bidderCode;
          }
        },
        {
          key: 'hb_adid',
          val: function (bidResponse) {
            return bidResponse.adId;
          }
        },
        {
          key: 'hb_pb',
          val: function (bidResponse) {
            return bidResponse.pbHg;
          }
        },
        {
          key: 'hb_size',
          val: function (bidResponse) {
            return bidResponse.size;
          }
        },
        {
          key: 'hb_source',
          val: function (bidResponse) {
            return bidResponse.source;
          }
        },
        {
          key: 'hb_format',
          val: function (bidResponse) {
            return bidResponse.mediaType;
          }
        }
      ]
    }
  };
}

module.exports = { configurePrebid };
